import { domain } from "../auth/domain";

const handleResponse = async (response, data) => {
  if (!response.ok) {
    const error = data !== undefined ? data : await response.json();
    // Create a more detailed error message for the user from backend validation
    const errorMessages = Object.entries(error)
      .map(([key, value]) => {
        const formattedKey = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        const message = Array.isArray(value) ? value.join(', ') : value;
        return `${formattedKey}: ${message}`;
      })
      .join('; ');
    throw new Error(errorMessages || `Request failed with status: ${response.status}`);
  }
  return data !== undefined ? data : response.json();
};

/**
 * Creates authorization headers for API requests.
 * @returns {Object} The headers object with the JWT token.
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem("accessToken");
  return {
    "Content-Type": "application/json",
    Authorization: `JWT ${token}`,
  };
};

/**
 * Fetches all arrears invoices for the user's group.
 * @returns {Promise<Array>} A promise that resolves to an array of invoices.
 */
export const fetchArrearsInvoices = async () => {
  const headers = getAuthHeaders();
  const response = await fetch(`${domain}contributions/arrears-invoices/`, { headers });

  return handleResponse(response);
};

/**
 * Calls the backend to generate a new arrears invoice for a specific member.
 * @param {string} memberId - The ID of the member to generate an invoice for.
 * @returns {Promise<Object>} A promise that resolves to the newly created invoice object.
 */
export const generateArrearsInvoice = async (memberId) => {
  const headers = getAuthHeaders();
  const response = await fetch(`${domain}contributions/members/${memberId}/generate-arrears-invoice/`, {
    method: "POST",
    headers,
  });

  return handleResponse(response);
};


/**
 * Fetches the currently logged-in member's single pending arrears invoice.
 * @returns {Promise<Object|null>} Latest pending invoice, or null only for a confirmed empty state.
 */
export const fetchMyArrearsInvoice = async () => {
  const headers = getAuthHeaders();
  const response = await fetch(`${domain}contributions/my-arrears-invoice/`, { headers });

  const data = await response.json();
  if (response.status === 404 && data?.code === "NO_PENDING_ARREARS") return null;
  const invoice = await handleResponse(response, data);
  if (!invoice?.invoice_id || invoice.current_total_due == null) {
    throw new Error("Unexpected arrears invoice response.");
  }
  return invoice;
};
